import { getAttendanceDisplayValue } from '@/components/feedbacks/session-feedback-utils.js';
import { StudentShortLink } from '@/components/join-now/student-short-link.js';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { Tooltip } from '@/components/tooltip.js';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination.js';
import { stringIfNull } from '@/lib/string-utils.js';
import { associateBy } from '@airreading/common';
import {
  AdminSession,
  SessionFeedbackWithAssessment,
  studentYearOption,
  toSessionFeedbackCompoundId,
  UserLike,
} from '@airreading/common/data';
import { Link } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';

import { useMemo } from 'react';

const SESSION_DETAIL_STUDENTS_PREFIX = 'session-detail-students';

interface SessionStudentsTableProps {
  session: AdminSession;
  feedbacks: SessionFeedbackWithAssessment[];
  isLoading?: boolean;
}

function studentsTableCellTestId(field: string, rowNumber: number): string {
  return `${SESSION_DETAIL_STUDENTS_PREFIX}-table-cell-${field}-${rowNumber}`;
}

function studentsTableRowTestId(element: string, rowNumber: number): string {
  return `${SESSION_DETAIL_STUDENTS_PREFIX}-table-${element}-${rowNumber}`;
}

const columnHelper = createColumnHelper<UserLike>();
const buildColumns = (session: AdminSession, feedbacksByStudentId: Map<string, SessionFeedbackWithAssessment>) => {
  return [
    columnHelper.accessor('fullNameOrEmail', {
      id: 'student',
      header: 'Student',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        return <span data-testid={studentsTableCellTestId('student', rowNumber)}>{getValue()}</span>;
      },
    }),
    columnHelper.accessor((row) => (row as UserLike & { shortCode?: string }).shortCode, {
      header: 'Direct join short link',
      id: 'shortLink',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        const shortLink = getValue();
        return (
          <StudentShortLink
            shortCode={shortLink ?? null}
            linkTestId={studentsTableRowTestId('link-short-code', rowNumber)}
            copyButtonTestId={studentsTableRowTestId('button-copy-short-code', rowNumber)}
            downloadButtonTestId={studentsTableRowTestId('button-download-qr', rowNumber)}
            emptyTestId={studentsTableCellTestId('short-link', rowNumber)}
          />
        );
      },
    }),
    columnHelper.display({
      id: 'org',
      header: 'District',
      cell: ({ row }) => (
        <span data-testid={studentsTableCellTestId('district', row.index + 1)}>{session.school.org.name}</span>
      ),
    }),
    columnHelper.display({
      id: 'school',
      header: 'School',
      cell: ({ row }) => (
        <span data-testid={studentsTableCellTestId('school', row.index + 1)}>{session.school.name}</span>
      ),
    }),
    columnHelper.accessor('id', {
      id: 'grades',
      header: 'Grade',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        const grade = session.gradeByStudentId[getValue()];
        return (
          <span data-testid={studentsTableCellTestId('grade', rowNumber)}>
            {studentYearOption(stringIfNull(grade))}
          </span>
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'attendance',
      header: 'Attendance',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        const studentId = getValue();
        const feedback = feedbacksByStudentId.get(studentId);
        if (!feedback) {
          return <span data-testid={studentsTableCellTestId('attendance', rowNumber)}>-</span>;
        }
        return (
          <span data-testid={studentsTableCellTestId('attendance', rowNumber)}>
            {getAttendanceDisplayValue(feedback)}
          </span>
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'feedbackStatus',
      header: 'Feedback',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        const studentId = getValue();
        const feedback = feedbacksByStudentId.get(studentId);
        return (
          <SessionFeedbackStatus
            feedbacks={feedback ? [feedback] : []}
            hoursCanceledBeforeStart={session.hoursCanceledBeforeStart}
            lessonShouldSkipFeedback={session.lesson.shouldSkipFeedback}
            testId={studentsTableCellTestId('feedback', rowNumber)}
          />
        );
      },
    }),
    columnHelper.accessor('id', {
      id: 'linkToFeedback',
      header: '',
      cell: ({ getValue, row }) => {
        const rowNumber = row.index + 1;
        const studentId = getValue();
        const feedback = feedbacksByStudentId.get(studentId);

        if (!feedback) {
          return <span data-testid={studentsTableCellTestId('feedback-link', rowNumber)}>-</span>;
        }
        return (
          <Link
            to="/sessions/session-feedbacks/$feedbackId"
            params={{ feedbackId: toSessionFeedbackCompoundId(feedback) }}
            search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          >
            <Tooltip
              trigger={<PencilIcon size={16} data-testid={studentsTableRowTestId('button-edit-feedback', rowNumber)} />}
            >
              <div>View/edit feedback</div>
            </Tooltip>
          </Link>
        );
      },
    }),
  ];
};

export const SessionStudentsTable = ({ session, feedbacks, isLoading = false }: SessionStudentsTableProps) => {
  const feedbacksByStudentId = associateBy(feedbacks, (feedback) => feedback.studentId);
  const columns = useMemo(() => buildColumns(session, feedbacksByStudentId), [session, feedbacksByStudentId]);
  const table = useReactTable({
    data: session.students,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return <EntityTable table={table} emptyState="No students found for this session." isLoading={isLoading} />;
};
